import { WorkspaceCreateField } from "@/components/shared/workspace-create-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUomUsage } from "@/hooks/use-uoms";

import { categoryOptions } from "./uoms";
import { useUomForm } from "./use-uom-form";

import type { UnitOfMeasurement } from "@artisancode/api-types";

export function UomDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: UnitOfMeasurement | null;
}) {
  const { form, isSubmitting, onSubmit } = useUomForm(open, editing, () =>
    onOpenChange(false),
  );
  const { data: usageData } = useUomUsage(editing?.id);
  const isCategoryLocked = !!editing && (usageData?.inUse ?? false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Satuan" : "Tambah Satuan"}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form id="uom-form" onSubmit={onSubmit} className="grid gap-3 py-2">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nama Satuan</FormLabel>
                  <FormControl>
                    <Input placeholder="Nama satuan" autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="symbol"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Simbol</FormLabel>
                  <FormControl>
                    <Input placeholder="kg, gram, sak, unit, dll." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kategori</FormLabel>
                  <Select
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={isCategoryLocked || isSubmitting}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Pilih kategori" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categoryOptions.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isCategoryLocked && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {usageData?.reason ||
                        "Kategori tidak dapat diubah karena satuan sudah memiliki konversi atau sedang digunakan."}
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
            <WorkspaceCreateField
              control={form.control}
              name="workspaceId"
              editing={!!editing}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Batal
          </Button>
          <Button type="submit" form="uom-form" disabled={isSubmitting}>
            {isSubmitting ? "Menyimpan..." : editing ? "Simpan" : "Tambah"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
